import { Download, ExternalLink } from 'lucide-react';

import { useTranslation } from '@/components/i18n/Translator';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';

interface BillingHistoryItem {
  id: string;
  date: string;
  amount: string;
  status: 'Paid' | 'Pending' | 'Failed' | 'Refunded';
  invoice: string;
  description?: string;
  downloadUrl?: string;
}

interface BillingHistoryTableProps {
  items: BillingHistoryItem[];
  onDownloadInvoice?: (invoiceId: string) => void;
  onViewInvoice?: (invoiceId: string) => void;
  isLoading?: boolean;
}

const BillingHistoryTable = ({
  items,
  onDownloadInvoice,
  onViewInvoice,
  isLoading = false
}: BillingHistoryTableProps) => {
  const { t } = useTranslation();
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Paid':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'Pending':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'Failed':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'Refunded':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        {/* Table Header - Desktop Only */}
        <div className="hidden lg:grid grid-cols-12 gap-4 px-4 py-3 bg-muted/50 rounded-lg text-sm font-medium text-muted-foreground">
          <div className="col-span-3">{t('billing.history.table.date')}</div>
          <div className="col-span-3">
            {t('billing.history.table.description')}
          </div>
          <div className="col-span-2">{t('billing.history.table.amount')}</div>
          <div className="col-span-2">{t('billing.history.table.status')}</div>
          <div className="col-span-2">{t('billing.history.table.actions')}</div>
        </div>

        {/* Loading skeleton */}
        <div className="space-y-2">
          {[1, 2, 3].map((i) => (
            <Card key={i} className="animate-pulse">
              <CardContent className="p-4">
                <div className="hidden lg:grid grid-cols-12 gap-4 items-center">
                  <div className="col-span-3 space-y-2">
                    <div className="h-4 bg-muted rounded w-24"></div>
                    <div className="h-3 bg-muted rounded w-20"></div>
                  </div>
                  <div className="col-span-3">
                    <div className="h-4 bg-muted rounded w-32"></div>
                  </div>
                  <div className="col-span-2">
                    <div className="h-4 bg-muted rounded w-16"></div>
                  </div>
                  <div className="col-span-2">
                    <div className="h-6 bg-muted rounded w-12"></div>
                  </div>
                  <div className="col-span-2">
                    <div className="flex space-x-1">
                      <div className="h-8 w-8 bg-muted rounded"></div>
                      <div className="h-8 w-8 bg-muted rounded"></div>
                    </div>
                  </div>
                </div>
                <div className="lg:hidden space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="space-y-2">
                      <div className="h-4 bg-muted rounded w-24"></div>
                      <div className="h-3 bg-muted rounded w-20"></div>
                    </div>
                    <div className="space-y-2">
                      <div className="h-4 bg-muted rounded w-16"></div>
                      <div className="h-6 bg-muted rounded w-12"></div>
                    </div>
                  </div>
                  <div className="h-4 bg-muted rounded w-32"></div>
                  <div className="flex justify-end space-x-2">
                    <div className="h-8 bg-muted rounded w-16"></div>
                    <div className="h-8 bg-muted rounded w-20"></div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Table Header - Desktop Only */}
      <div className="hidden lg:grid grid-cols-12 gap-4 px-4 py-3 bg-muted/50 rounded-lg text-sm font-medium text-muted-foreground">
        <div className="col-span-3">{t('billing.history.table.date')}</div>
        <div className="col-span-3">
          {t('billing.history.table.description')}
        </div>
        <div className="col-span-2">{t('billing.history.table.amount')}</div>
        <div className="col-span-2">{t('billing.history.table.status')}</div>
        <div className="col-span-2">{t('billing.history.table.actions')}</div>
      </div>

      {/* Table Rows */}
      <div className="space-y-2">
        {items.map((item) => (
          <Card key={item.id} className="hover:shadow-md transition-shadow">
            <CardContent className="p-4">
              {/* Desktop Layout */}
              <div className="hidden lg:grid grid-cols-12 gap-4 items-center">
                {/* Date */}
                <div className="col-span-3">
                  <div className="font-medium text-sm">{item.date}</div>
                  <div className="text-xs text-muted-foreground">
                    {item.invoice}
                  </div>
                </div>

                {/* Description */}
                <div className="col-span-3">
                  <div className="text-sm">
                    {item.description ||
                      t('billing.history.table.defaultDescription')}
                  </div>
                </div>

                {/* Amount */}
                <div className="col-span-2">
                  <div className="font-semibold text-sm">{item.amount}</div>
                </div>

                {/* Status */}
                <div className="col-span-2">
                  <Badge
                    variant="outline"
                    className={`${getStatusColor(
                      item.status
                    )} text-xs font-medium`}
                  >
                    {item.status}
                  </Badge>
                </div>

                {/* Actions */}
                <div className="col-span-2">
                  <div className="flex items-center space-x-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0"
                      onClick={() => onViewInvoice?.(item.id)}
                      title={t('billing.history.table.viewInvoice')}
                    >
                      <ExternalLink className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0"
                      onClick={() => onDownloadInvoice?.(item.id)}
                      title={t('billing.history.table.downloadInvoice')}
                    >
                      <Download className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>

              {/* Mobile Layout */}
              <div className="lg:hidden space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-medium text-sm">{item.date}</div>
                    <div className="text-xs text-muted-foreground">
                      {item.invoice}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-semibold text-sm">{item.amount}</div>
                    <Badge
                      variant="outline"
                      className={`${getStatusColor(
                        item.status
                      )} text-xs font-medium`}
                    >
                      {item.status}
                    </Badge>
                  </div>
                </div>
                <div className="text-sm text-muted-foreground">
                  {item.description ||
                    t('billing.history.table.defaultDescription')}
                </div>
                <div className="flex items-center justify-end space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onViewInvoice?.(item.id)}
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    {t('billing.history.table.view')}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onDownloadInvoice?.(item.id)}
                  >
                    <Download className="w-4 h-4 mr-2" />
                    {t('billing.history.table.download')}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Empty State */}
      {items.length === 0 && (
        <div className="text-center py-12">
          <div className="text-muted-foreground">
            {t('billing.history.table.noData')}
          </div>
          <div className="text-sm text-muted-foreground mt-1">
            {t('billing.history.table.noDataDescription')}
          </div>
        </div>
      )}

      {/* Load More */}
      {items.length > 0 && (
        <div className="text-center pt-4">
          <Button variant="outline" size="sm">
            {t('billing.history.table.loadMore')}
          </Button>
        </div>
      )}
    </div>
  );
};

export default BillingHistoryTable;
